import { useEffect, useMemo, useState } from 'react';
import { firstYouTubeVideoId, youtubeThumbnail, fetchYouTubeInfo } from '../../lib/youtubeLinks';
import { VIDEO_CATEGORIES } from '../../lib/videoLibrary';

// "Also suggest this video for the video library" — shown in a composer only
// while the post contains a YouTube video link. Used by BOTH compose surfaces
// (CreatePostModal and the table detail screen's inline form).
//
// Controlled: the parent owns `value` ({ checked, category }) and receives the
// resolved `suggestion` ({ videoId, title, category } or null) through
// onResolve, which is what it passes to suggestVideoFromPost after posting.
// The title comes from oEmbed; without one the video can't be submitted
// (submit_video requires it), so a private or deleted video shows why instead
// of a checkbox that would fail.
export const EMPTY_SUGGESTION = { checked: false, category: '' };

const inputStyle = {
  backgroundColor: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  color: 'var(--text-primary)',
};

const SuggestVideoToLibrary = ({ subject, content, value, onChange, onResolve }) => {
  const videoId = useMemo(() => firstYouTubeVideoId(subject, content), [subject, content]);
  const [info, setInfo] = useState({ id: null, title: null, loading: false });

  useEffect(() => {
    if (!videoId) { setInfo({ id: null, title: null, loading: false }); return undefined; }
    let cancelled = false;
    setInfo({ id: videoId, title: null, loading: true });
    fetchYouTubeInfo(videoId).then((result) => {
      if (!cancelled) setInfo({ id: videoId, title: result?.title ?? null, loading: false });
    });
    return () => { cancelled = true; };
  }, [videoId]);

  const ready = !!videoId && info.id === videoId && !!info.title;

  useEffect(() => {
    onResolve(value.checked && ready
      ? { videoId, title: info.title, category: value.category }
      : null);
  }, [value.checked, value.category, ready, videoId, info.title, onResolve]);

  if (!videoId) return null;

  return (
    <div className="rounded-xl p-3 space-y-2" style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}>
      <div className="flex items-start gap-3">
        <img
          src={youtubeThumbnail(videoId)}
          alt=""
          className="w-20 aspect-video rounded-md object-cover flex-shrink-0 bg-black"
        />
        <div className="min-w-0 flex-1">
          {info.loading ? (
            <p className="text-xs text-tertiary">Looking up the video…</p>
          ) : ready ? (
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 flex-shrink-0"
                style={{ accentColor: 'var(--brand-primary)' }}
                checked={value.checked}
                onChange={(e) => onChange({ ...value, checked: e.target.checked })}
              />
              <span className="text-sm text-primary leading-snug">
                Also suggest this video for the video library
                <span className="block text-xs text-tertiary mt-0.5 line-clamp-1">{info.title}</span>
              </span>
            </label>
          ) : (
            <p className="text-xs text-tertiary">
              This video couldn't be looked up (it may be private), so it can't be suggested for the library.
            </p>
          )}
        </div>
      </div>

      {ready && value.checked && (
        <div className="space-y-1">
          <select
            value={value.category}
            onChange={(e) => onChange({ ...value, category: e.target.value })}
            aria-label="Library category"
            className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
            style={inputStyle}
          >
            <option value="">Choose a library category</option>
            {VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <p className="text-xs text-tertiary">
            Your post goes up right away. The video joins the library after a moderator approves it.
          </p>
        </div>
      )}
    </div>
  );
};

export default SuggestVideoToLibrary;
